import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getHighResImageUrl } from "../utils/torrentImages";

const SWIPE_THRESHOLD = 48;

export default function TorrentImageGallery({
  images = [],
  alt = "",
  className = "",
  style,
  imageClassName = "",
  imageStyle,
  showDots = true,
  showArrows = true,
  dotContainerClassName = "",
  dotContainerStyle,
  activeDotStyle,
  inactiveDotStyle,
}) {
  const resolvedImages = useMemo(
    () => images.map(getHighResImageUrl).filter(Boolean),
    [images],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const pointerStartRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    setCurrentIndex((prevIndex) => {
      if (resolvedImages.length === 0) {
        return 0;
      }

      return Math.min(prevIndex, resolvedImages.length - 1);
    });
  }, [resolvedImages.length]);

  useEffect(() => {
    if (!suppressClickRef.current) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [currentIndex]);

  const shiftSlide = (delta) => {
    setCurrentIndex((prevIndex) => {
      if (resolvedImages.length <= 1) {
        return 0;
      }

      return (
        (prevIndex + delta + resolvedImages.length) % resolvedImages.length
      );
    });
  };

  const handlePointerDown = (e) => {
    if (resolvedImages.length <= 1) {
      return;
    }

    pointerStartRef.current = { x: e.clientX, y: e.clientY };

    if (e.pointerType === "touch") {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };

  const handlePointerUp = (e) => {
    if (resolvedImages.length <= 1 || !pointerStartRef.current) {
      pointerStartRef.current = null;
      return;
    }

    const deltaX = e.clientX - pointerStartRef.current.x;
    const deltaY = e.clientY - pointerStartRef.current.y;
    pointerStartRef.current = null;

    if (
      Math.abs(deltaX) < SWIPE_THRESHOLD ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    suppressClickRef.current = true;
    shiftSlide(deltaX < 0 ? 1 : -1);
  };

  const handleClickCapture = (e) => {
    if (!suppressClickRef.current) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    suppressClickRef.current = false;
  };

  const handleArrowClick = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    suppressClickRef.current = true;
    shiftSlide(delta);
  };

  if (resolvedImages.length === 0) {
    return null;
  }

  const canNavigate = showArrows && resolvedImages.length > 1;

  const arrowButtonStyle = {
    position: "absolute",
    top: "42%",
    transform: "translateY(-50%)",
    zIndex: 5,
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    background: "rgba(0, 0, 0, 0.28)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    color: "rgba(255, 255, 255, 0.82)",
    cursor: "pointer",
    padding: 0,
    transition: "opacity 180ms ease, background 180ms ease, color 180ms ease",
    opacity: 0.72,
  };

  return (
    <div
      className={className}
      style={{
        position: "relative",
        overflow: "hidden",
        touchAction: resolvedImages.length > 1 ? "pan-y" : undefined,
        ...style,
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        pointerStartRef.current = null;
      }}
      onClickCapture={handleClickCapture}
    >
      <div
        style={{
          display: "flex",
          width: `${resolvedImages.length * 100}%`,
          height: "100%",
          transform: `translateX(-${currentIndex * (100 / resolvedImages.length)}%)`,
          transition: "transform 260ms ease",
        }}
      >
        {resolvedImages.map((imageUrl, index) => (
          <img
            key={`${imageUrl}-${index}`}
            src={imageUrl}
            alt={resolvedImages.length > 1 ? `${alt} ${index + 1}` : alt}
            className={imageClassName}
            style={{
              width: `${100 / resolvedImages.length}%`,
              flex: `0 0 ${100 / resolvedImages.length}%`,
              ...imageStyle,
            }}
            draggable={false}
          />
        ))}
      </div>

      {canNavigate && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => handleArrowClick(e, -1)}
            onPointerDown={(e) => e.stopPropagation()}
            className="torrent-gallery-arrow"
            style={{
              ...arrowButtonStyle,
              left: 8,
            }}
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => handleArrowClick(e, 1)}
            onPointerDown={(e) => e.stopPropagation()}
            className="torrent-gallery-arrow"
            style={{
              ...arrowButtonStyle,
              right: 8,
            }}
          >
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </>
      )}

      {showDots && resolvedImages.length > 1 && (
        <div
          className={dotContainerClassName}
          style={{
            position: "absolute",
            left: "50%",
            bottom: 12,
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            zIndex: 4,
            ...dotContainerStyle,
          }}
        >
          {resolvedImages.map((_, index) => {
            const isActive = index === currentIndex;

            return (
              <button
                key={index}
                type="button"
                aria-label={`Show image ${index + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
                style={{
                  width: isActive ? 9 : 7,
                  height: isActive ? 9 : 7,
                  borderRadius: "999px",
                  border: "none",
                  padding: 0,
                  background: isActive
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(255, 255, 255, 0.68)",
                  boxShadow: isActive
                    ? "0 0 14px rgba(255, 255, 255, 0.48), 0 0 3px rgba(255, 255, 255, 0.95)"
                    : "0 0 8px rgba(255, 255, 255, 0.18)",
                  cursor: "pointer",
                  transition: "all 180ms ease",
                  ...(isActive ? activeDotStyle : inactiveDotStyle),
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
